import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonRadio,
  IonSelect,
  IonSelectOption,
  IonText,
} from '@ionic/react';
import range from 'lodash/range';
import { formatCurrency } from 'utils/currency';
import api_v2 from 'services/api_v2';
import { useSelector } from 'react-redux';
import { AuthSelectors } from 'redux/AuthRedux';
import { AppSelectors } from 'redux/AppRedux';
import ItemDescModal from './ItemDescModal';

export const MAX_DESC_LENGTH = 170;

function MenuItemDetail({
  item,
  quantity,
  onChangeQuantity,
  modifierData,
  setModifierData,
  setGroupId,
  setButtonEnable
}) {
  const token = useSelector(AuthSelectors.selectToken);
  const venue = useSelector(AppSelectors.selectVenue);
  const [detail, setDetail] = useState(false);
  const [itemWithModifiers, setItemWithModifiers] = useState();

  const hasMore = (item.description || '').length > MAX_DESC_LENGTH;
  const description = hasMore
    ? `${item.description.substr(0, MAX_DESC_LENGTH)}...`
    : item.description;
  const hasQuantity = !!onChangeQuantity;

  useEffect(() => {
    async function getItemDetails(itemId, venueId) {
      try {
        const api = api_v2(token);
        const details = await api.getMenuItem(venueId, itemId);
        if (details) {
          setGroupId(details.groupId);
          setItemWithModifiers(details);

          if (!details.modifiers || details.modifiers.length === 0) {
            setButtonEnable(true);
          } else {
            const existingRequiredFieldItem = details.modifiers.filter(ii => ii.isRequired === true);

            if (existingRequiredFieldItem.length === 0) {
              setButtonEnable(true);
            }
          }
        }
      } catch (error) {
        console.log('get menu item detail info error', error);
      }
    }

    if (item && venue) getItemDetails(item.id, venue);
  }, [item, venue]);

  const radioItemChecked = (id, checked) => {
    if (checked) {
      document.getElementById(id).classList.add('radio-checked');
      document.getElementById(id).ariaChecked = true;
    } else {
      document.getElementById(id).classList.remove('radio-checked');
      document.getElementById(id).ariaChecked = false;
    }
  };

  const radioItemShow = (id, visible) => {
    if (visible) {
      document.getElementById(`div-${id}`).classList.remove('radio-item-hide');
      document.getElementById(`div-${id}`).classList.add('radio-item-show');
    } else {
      document.getElementById(`div-${id}`).classList.remove('radio-item-show');
      document.getElementById(`div-${id}`).classList.add('radio-item-hide');
    }
  };

  const onRadioItemChange = (modifier, parentId) => {
    const formattedModifierItem = {
      item: modifier,
      id: modifier.id,
      groupId: parentId,
    };

    const isExistingItem = modifierData.filter(
      (optionItem) => optionItem.id === formattedModifierItem.id
    );

    if (isExistingItem.length === 0) {
      radioItemChecked(modifier.id, true);

      const isExistingItemPos = modifierData
        .map((optionItem) => optionItem.groupId)
        .indexOf(formattedModifierItem.groupId);

      if (isExistingItemPos === -1) {
        if (modifier.modifiers) {
          radioItemShow(modifier.modifiers.id, true);
        }

        setModifierData([...modifierData, formattedModifierItem]);
      } else {
        const originalModifier = modifierData[isExistingItemPos];
        radioItemChecked(originalModifier.item.id, false);

        if (originalModifier.item.modifiers) {
          radioItemShow(originalModifier.item.modifiers.id, false);
        }

        if (formattedModifierItem.item.modifiers) {
          radioItemShow(formattedModifierItem.item.modifiers.id, true);
        }

        modifierData[isExistingItemPos] = formattedModifierItem;
        setModifierData(modifierData);
      }
    }
  };

  const onCheckItemChange = (modifier, parentId) => {
    const formattedModifierItem = {
      item: modifier,
      id: modifier.id,
      groupId: parentId,
    };

    const isExistingItem = modifierData.filter(
      (optionItem) => optionItem.id === formattedModifierItem.id
    );

    if (isExistingItem.length === 0) {
      setModifierData([...modifierData, formattedModifierItem]);
    } else {
      setModifierData(
        modifierData.filter(
          (optionItem) => optionItem.id !== formattedModifierItem.id
        )
      );
    }
  };

  const checkValidation = () => {
    if (itemWithModifiers && itemWithModifiers.modifiers) {
      const isValidateArr = [];

      for (let i = 0; i < itemWithModifiers.modifiers.length; i += 1) {
        const modifier = itemWithModifiers.modifiers[i];

        let isSelected = false;

        if (modifier.isRequired) {
          for (let j = 0; j < modifierData.length; j += 1) {
            if (modifier.options.length > 0) {
              const isExistingItem = modifier.options.filter(it => it.id === modifierData[j].id);

              if (isExistingItem.length > 0) {
                isSelected = true;
                break;
              }
            } else {
              isSelected = true;
              break;
            }
          }
        } else {
          let existingItemCount = 0;

          for (let j = 0; j < modifierData.length; j += 1) {
            if (modifier.options.length > 0) {
              const isExistingItem = modifier.options.filter(it => it.id === modifierData[j].id);

              if (isExistingItem.length > 0) {
                existingItemCount += 1;
              }
            }
          }

          if (modifier.maxSelection) {
            if (existingItemCount > modifier.maxSelection) {
              isSelected = false;
            } else {
              isSelected = true;
            }
          } else {
            isSelected = true;
          }
        }

        isValidateArr.push(isSelected);
      }

      const isValidCheck = isValidateArr.every(v => v === true);

      console.log("isValid Result: ", itemWithModifiers.modifiers, isValidateArr, isValidCheck);
      setButtonEnable(isValidCheck);
    }
  }

  useEffect(() => {
    checkValidation();
  }, [modifierData]);

  const renderRadio = (data, isNested = false) => {
    return (
      <div
        id={`div-${data.id}`}
        className={isNested ? 'ml-3 radio-item-hide' : 'radio-item-show'}
      >
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <IonText color="primary">
              <h3>
                <b>{data.name}</b>
              </h3>
            </IonText>

            {
              data.isRequired &&
              <IonText color="danger">
                <h6>Required</h6>
              </IonText>
            }

          </IonLabel>
        </IonItem>

        {data.options.map((modifier, ii) => {
          return (
            <div key={ii}>
              <div className="loyalty-coin-wrap ml-5 mt-2x">
                <IonRadio
                  id={modifier.id}
                  slot="start"
                  value={modifier.id}
                  color="tertiary"
                  mode="md"
                  className="loyalty-radio"
                  onClick={() => onRadioItemChange(modifier, data.id, isNested)}
                />

                <IonLabel color="primary">{modifier.name}</IonLabel>

                {modifier.price !== 0 && (
                  <IonNote
                    slot="end"
                    color="primary"
                    className="loyalty-menuitem-price"
                  >
                    + {formatCurrency(modifier.price)}
                  </IonNote>
                )}
              </div>

              {
                // modifier.modifiers && renderRadio(modifier.modifiers, true)
              }
            </div>
          );
        })}
      </div>
    );
  };

  const renderCheckBox = (data) => {
    return (
      <div>
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <IonText color="primary">
              <h3>
                <b>{data.name}</b>
              </h3>
            </IonText>

            {
              data.isRequired &&
              <IonText color="danger">
                <h6>Required</h6>
              </IonText>
            }

            {
              data.maxSelection &&
              <IonText color="primary">
                <h6>Choose up to {data.maxSelection}</h6>
              </IonText>
            }

          </IonLabel>
        </IonItem>

        {data.options.map((modifier) => {
          return (
            <div key={modifier.id} className="loyalty-coin-wrap mt-2x ml-5">
              <IonCheckbox
                slot="start"
                value={modifier}
                color="tertiary"
                mode="md"
                className="loyalty-radio"
                onClick={() => {
                  onCheckItemChange(modifier, data.id);
                }}
              />

              <IonLabel color="primary">{modifier.name}</IonLabel>

              {modifier.price !== 0 && (
                <IonNote
                  slot="end"
                  color="primary"
                  className="loyalty-menuitem-price"
                >
                  + {formatCurrency(modifier.price)}
                </IonNote>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <IonList>
      {item.image && (
        <IonItem lines="none">
          <div className="menu-item-img-wrapper">
            <img src={item.image} alt={item.name} />
          </div>
        </IonItem>
      )}
      <IonItem lines={hasQuantity ? 'full' : 'none'} className="mb-1x">
        <IonLabel className="ion-text-wrap">
          {hasQuantity ? (
            <p>
              {description}
              {hasMore && <br />}
              {hasMore && <a onClick={() => setDetail(true)}>See more</a>}
            </p>
          ) : (
            <p>{item.description}</p>
          )}
        </IonLabel>
      </IonItem>

      {hasQuantity &&
        itemWithModifiers &&
        itemWithModifiers.modifiers &&
        itemWithModifiers.modifiers.map((modifier) => {
          return modifier.isRequired && modifier.maxSelection === 1
            ? renderRadio(modifier)
            : renderCheckBox(modifier);
        })}

      {hasQuantity && (
        <IonItem className="mt-2x">
          <IonLabel slot="start" className="ion-text-wrap">
            <div className="ion-text-uppercase text-bold">Quantity</div>
          </IonLabel>
          <IonSelect
            slot="end"
            value={+quantity}
            interface="alert"
            onIonChange={(e) => onChangeQuantity(e.detail.value)}
          >
            {range(1, 6).map((r) => (
              <IonSelectOption key={`quantity_${r}`} value={r}>
                {r}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
      )}

      <ItemDescModal onClose={() => setDetail(false)} isOpen={detail}>
        {item.description}
      </ItemDescModal>
    </IonList>
  );
}

MenuItemDetail.propTypes = {
  item: PropTypes.object,
  onChangeQuantity: PropTypes.func,
  quantity: PropTypes.any,
  modifierData: PropTypes.array,
  setModifierData: PropTypes.func,
  setGroupId: PropTypes.func,
  setButtonEnable: PropTypes.func,
};

export default MenuItemDetail;
